/** Endpoints #20-#21 — the settings registry (§4.1) and its DTO shape. */
import { z } from 'zod';
import { POST_TYPES } from '../enums.js';
import { AI_SETTINGS_DEFAULT } from './ai.js';
import { NOTIFICATIONS_SETTINGS_DEFAULT, NotificationsSettingsSchema, } from './notifications.js';
import { NOTION_SETTINGS_DEFAULT } from './notion.js';
import { TELEGRAM_SETTINGS_DEFAULT } from './telegram.js';
import { WATCH_SETTINGS_DEFAULT } from './watch.js';
/* ------------------------------------------------------- registry (§4.1) */
export const SCRAPER_CONFIG_PREFIX = 'scraperConfig.';
/** Key of a plugin's config block, e.g. `scraperConfig.apify-posts`. */
export function scraperConfigKey(pluginId) {
    return `${SCRAPER_CONFIG_PREFIX}${pluginId}`;
}
/** Plugin ids the default registry ships (§7.3) — the migration and the seeds key off these. */
export const DEFAULT_PLUGIN_IDS = ['apify-profile', 'apify-posts', 'apify-company'];
/**
 * Non-prefixed keys of the `settings` table (§4.1). `activeScrapers` lives here but is read and
 * written through #35/#38, not through the settings endpoints.
 */
export const SETTING_KEYS = [
    'activeScrapers',
    'backfillMonths',
    'rescrapeDays',
    'scrapeHour',
    'timezone',
    'mediaEnabled',
    'countedPostTypes',
    'appBaseUrl',
    'notion',
    'notifications',
    'telegram',
    'watch',
    'telegramWatch',
    'ai',
];
/** Defaults applied when a key is absent from the table (§4.1). */
export const SETTINGS_DEFAULTS = {
    activeScrapers: {
        profile: 'apify-profile',
        posts: 'apify-posts',
        company: 'apify-company',
    },
    backfillMonths: 12,
    rescrapeDays: 30,
    scrapeHour: 3,
    /** `null` → frontend writes the browser tz on first load; server falls back to UTC (A-5). */
    timezone: null,
    mediaEnabled: false,
    countedPostTypes: ['original', 'repost_with_comment', 'article'],
    /**
     * Public origin of this app, shared by the Notion deep links and the Telegram View button.
     * Promoted out of the `notion` blob (SPEC `telegram` § Data model) — one URL, two consumers.
     */
    appBaseUrl: null,
    /** Save to Notion is off until the tab is filled in (#40-#47). */
    notion: NOTION_SETTINGS_DEFAULT,
    /** Viral-alert thresholds (#48-#50): all five rules armed at 5× on a fresh install. */
    notifications: NOTIFICATIONS_SETTINGS_DEFAULT,
    /** Telegram push delivery is off until the handshake completes (#52-#55). */
    telegram: TELEGRAM_SETTINGS_DEFAULT,
    /** New Post Watching (#68-#72): the master toggle is off on a fresh install. Written by #69. */
    watch: WATCH_SETTINGS_DEFAULT,
    /**
     * The **second** Telegram binding — the watch bot (#73-#76). Same shape as `telegram` and the
     * same decoder: a separate chat is what buys a separate phone sound from the viral alerts.
     */
    telegramWatch: TELEGRAM_SETTINGS_DEFAULT,
    /**
     * Comment Generation (#77-#85): written only by #80 (the active backend) and #81 (the prompt
     * pair), never by #21 — the `telegram`/`watch` precedent. `activeBackend: null` = disabled.
     */
    ai: AI_SETTINGS_DEFAULT,
};
/** Server-side fallback while `timezone` is `null` (ASSUMPTION A-5). */
export const FALLBACK_TIMEZONE = 'UTC';
/* -------------------------------------------------------- update (#21) */
/**
 * Partial update. Side effects (§5.1 #21): timezone/scrapeHour → reschedule;
 * countedPostTypes → enqueue a `recompute_baselines` job. `notifications` deliberately has none —
 * thresholds apply from each profile's next scrape, and nothing is retroactive (SPEC § When the
 * check runs).
 */
export const SettingsUpdateBodySchema = z
    .strictObject({
    backfillMonths: z.number().int().min(1).max(120).optional(),
    rescrapeDays: z.number().int().min(1).max(3650).optional(),
    scrapeHour: z.number().int().min(0).max(23).optional(),
    timezone: z.string().trim().min(1).max(100).nullable().optional(),
    mediaEnabled: z.boolean().optional(),
    countedPostTypes: z.array(z.enum(POST_TYPES)).min(1).optional(),
    notifications: NotificationsSettingsSchema.optional(),
})
    .refine((body) => Object.keys(body).length > 0, 'nothing to update');
//# sourceMappingURL=settings.js.map